// [목적] ⌘K/Ctrl+K 커맨드 팔레트 — 폴리오 검색(GET /folios?q=) + 결과 클릭/Enter 이동
// [이유] G1-4: Sidebar.tsx:277 검색 버튼이 "(후속 Spec)" placeholder 로만 존재하던 것을 실배선.
//        listFoliosQuerySchema.q(packages/types/src/folio.ts) 를 BE 가 지원하므로 별도 검색 엔드포인트
//        없이 기존 GET /folios?q= 를 그대로 재사용한다(ponytail — 신규 API 불요).
// [사이드 이팩트] 없음(순수 조회) — folioClient.listFolios({q, limit:8}) 1회성 호출, 디바운스 없음
//                (검색어가 짧고 결과가 적어 즉시 조회로도 충분, 후속 필요 시 debounce 추가).
// [수정 시 고려사항] 키보드 경로: ArrowUp/Down 으로 하이라이트 이동, Enter 로 선택 이동, Esc 는 MUI Modal
//                   기본 동작으로 닫힘(별도 핸들러 불필요). 결과 0건이면 안내 문구만 표시.

import { useEffect, useState } from 'react';
import { Box, InputBase, Modal, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { listFolios } from '../../api/folioClient.js';
import { queryKeys } from '../../api/queryKeys.js';

const c = tokens.color;
const L = 'light' as const;
const INK_HI = c.ink.high[L];
const INK_MID = c.ink.mid[L];
const INK_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER2 = c.paper[2][L];

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

/**
 * [목적] ⌘K 커맨드 팔레트 — 검색어 입력 → 폴리오 결과 목록(최대 8건) → 클릭/Enter 로 /folio/:id 이동
 * [이유] G1-4: Sidebar 검색 버튼 실배선. 별도 검색 API 가 없어 기존 피드 목록 API(q 파라미터)를 재사용.
 * [사이드 이팩트] 없음 — 모달 닫힐 때 검색어/하이라이트 인덱스 리셋(재오픈 시 항상 빈 상태로 시작).
 * [수정 시 고려사항] 결과 개수(limit=8) 변경 시 하이라이트 인덱스 clamp 로직도 함께 검토.
 */
export function CommandPalette({ open, onClose }: CommandPaletteProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const navigate = useNavigate();

  const trimmed = query.trim();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.folioList({ q: trimmed, limit: 8 }),
    queryFn: () => listFolios({ q: trimmed, limit: 8 }),
    enabled: trimmed.length > 0,
    staleTime: 1000 * 30,
    throwOnError: false,
  });
  const results = trimmed.length > 0 ? (data?.items ?? []) : [];

  // 모달 재오픈 시 항상 빈 검색 상태로 시작
  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
    }
  }, [open]);

  // 결과가 바뀌면 하이라이트를 범위 내로 clamp
  useEffect(() => {
    setHighlight((prev) => Math.min(prev, Math.max(results.length - 1, 0)));
  }, [results.length]);

  const handleSelect = (folioId: string): void => {
    onClose();
    navigate(`/folio/${folioId}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const picked = results[highlight];
      if (picked) handleSelect(picked.id);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-label="폴리오 검색"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        // [수정] 2026-09-18 QA(M-6): 뒤에 온 `p: '16px'` 단축 속성이 `pt: '12vh'` 를 덮어써서
        //        팔레트가 화면 최상단 16px 에 붙어 있었다. 단축 속성을 먼저 두고 pt 로 덮는다.
        //        순서를 되돌리면 같은 회귀가 재발한다.
        p: '16px',
        pt: '12vh',
      }}
    >
      <Box
        role="combobox"
        aria-expanded={results.length > 0}
        aria-owns="command-palette-listbox"
        aria-haspopup="listbox"
        sx={{
          bgcolor: PAPER0,
          borderRadius: `${tokens.radius.l}px`,
          width: '100%',
          maxWidth: 480,
          boxShadow: tokens.shadow.modal,
          outline: 'none',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            p: '14px 16px',
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Box component="span" aria-hidden="true" sx={{ fontSize: 14, color: INK_LOW }}>
            ⌕
          </Box>
          <InputBase
            autoFocus
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="폴리오 제목·무드 검색..."
            inputProps={{ 'aria-label': '폴리오 검색어 입력', role: 'searchbox' }}
            sx={{ fontSize: 13.5, color: INK_HI }}
          />
        </Box>

        <Box
          id="command-palette-listbox"
          role="listbox"
          aria-label="검색 결과"
          sx={{ maxHeight: 360, overflowY: 'auto' }}
        >
          {trimmed.length === 0 && (
            <Typography sx={{ p: '20px 16px', fontSize: 12, color: INK_LOW, textAlign: 'center' }}>
              검색어를 입력해 보세요.
            </Typography>
          )}
          {trimmed.length > 0 && isLoading && (
            <Typography sx={{ p: '20px 16px', fontSize: 12, color: INK_LOW, textAlign: 'center' }}>
              검색 중...
            </Typography>
          )}
          {trimmed.length > 0 && !isLoading && results.length === 0 && (
            <Typography sx={{ p: '20px 16px', fontSize: 12, color: INK_LOW, textAlign: 'center' }}>
              검색 결과가 없습니다.
            </Typography>
          )}
          {results.map((folio, idx) => (
            <Box
              key={folio.id}
              role="option"
              aria-selected={idx === highlight}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => handleSelect(folio.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                p: '10px 16px',
                cursor: 'pointer',
                bgcolor: idx === highlight ? PAPER2 : 'transparent',
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: INK_HI }}>
                {folio.title}
              </Typography>
              {folio.vibe.length > 0 && (
                <Typography sx={{ fontSize: 11, color: INK_MID }}>
                  {folio.vibe.join(' · ')}
                </Typography>
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: '12px', p: '8px 16px', borderTop: `1px solid ${BORDER}` }}>
          <Typography sx={{ fontSize: 10, color: INK_LOW }}>↑↓ 이동</Typography>
          <Typography sx={{ fontSize: 10, color: INK_LOW }}>↵ 이동</Typography>
          <Typography sx={{ fontSize: 10, color: INK_LOW }}>Esc 닫기</Typography>
        </Box>
      </Box>
    </Modal>
  );
}
